import React, { useState, useEffect } from 'react';
import Icon from 'components/AppIcon';
import api from 'lib/api';
import { useAuth } from 'contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import MembershipCard from './components/MembershipCard';
import AttendanceCalendar from './components/AttendanceCalendar';
import WorkoutDietPlans from './components/WorkoutDietPlans';
import InvoiceList from './components/InvoiceList';
import QRCodeCard from './components/QRCodeCard';
import ProfileCompletionCard from './components/ProfileCompletionCard';

export default function MemberPortal() {
  const { user, profile, signOut } = useAuth();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('overview');
  const [memberRecord, setMemberRecord] = useState(null);
  const [memberPackage, setMemberPackage] = useState(null);
  const [memberLoading, setMemberLoading] = useState(true);
  const [memberError, setMemberError] = useState(null);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [profileCardDismissed, setProfileCardDismissed] = useState(false);

  useEffect(() => {
    const fetchMemberData = async () => {
      setMemberLoading(true);
      setMemberError(null);
      try {
        const email = user?.email;
        if (!email) { setMemberLoading(false); return; }
        const { data: members } = await api.get('/members', { params: { search: email, limit: 1 } });
        const memberData = members?.[0] || null;
        setMemberRecord(memberData);
        if (memberData?.package_name) {
          setMemberPackage({
            id: memberData?.active_package_id,
            packageName: memberData?.package_name,
            packageType: memberData?.package_type,
            price: memberData?.package_price,
            startDate: memberData?.package_start_date,
            endDate: memberData?.package_end_date,
            isActive: true,
          });
        }
      } catch (err) {
        setMemberError('Failed to load member data');
      } finally {
        setMemberLoading(false);
      }
    };
    fetchMemberData();
  }, [user?.email]);

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await signOut();
      navigate('/login-page');
    } catch (err) {
      setLoggingOut(false);
      setShowLogoutModal(false);
    }
  };

  const memberName = memberRecord
    ? `${memberRecord?.first_name ?? ''} ${memberRecord?.last_name ?? ''}`?.trim()
    : profile?.full_name ?? user?.email ?? 'Member';

  const avatarInitials = memberName
    ?.split(' ')
    ?.slice(0, 2)
    ?.map(n => n?.[0]?.toUpperCase())
    ?.join('') ?? 'M';

  const sections = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'plans', label: 'My Plans', icon: 'ClipboardList' },
    { id: 'invoices', label: 'Invoices', icon: 'FileText' },
    { id: 'referrals', label: 'Referrals & QR', icon: 'Gift' },
  ];

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      <main className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 py-5 md:py-6 lg:py-8">
        {/* Member Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full overflow-hidden bg-gradient-to-br from-[var(--color-primary)] to-[var(--color-secondary)] flex items-center justify-center flex-shrink-0">
              {memberLoading ? (
                <div className="w-6 h-6 rounded-full bg-white/30 animate-pulse" />
              ) : profile?.avatar_url ? (
                <img src={profile?.avatar_url} alt="Member profile avatar" className="w-full h-full object-cover" />
              ) : (
                <span className="text-xl font-bold text-white font-[var(--font-heading)]">{avatarInitials}</span>
              )}
            </div>
            <div>
              {memberLoading ? (
                <>
                  <div className="h-6 w-40 bg-[var(--color-muted)] animate-pulse rounded mb-1" />
                  <div className="h-4 w-28 bg-[var(--color-muted)] animate-pulse rounded" />
                </>
              ) : (
                <>
                  <h1 className="text-xl md:text-2xl font-bold text-[var(--color-foreground)] font-[var(--font-heading)]">
                    Welcome back, {memberName?.split(' ')?.[0] ?? 'Member'}!
                  </h1>
                  <p className="text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-0.5">
                    {memberPackage?.packageName ?? 'No active plan'} · {memberRecord?.id ? memberRecord?.id?.substring(0, 8)?.toUpperCase() : '—'}
                  </p>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => navigate('/member-progress')}
              className="flex items-center gap-2 px-3 py-2 rounded-[var(--radius-sm)] border border-[var(--color-border)] text-sm text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] hover:border-[var(--color-primary)]/40 transition-all font-[var(--font-caption)]"
              title="My Progress"
            >
              <Icon name="TrendingUp" size={14} color="currentColor" />
              <span className="hidden sm:inline">My Progress</span>
            </button>
            <button
              onClick={() => navigate('/account-settings')}
              className="flex items-center gap-2 px-3 py-2 rounded-[var(--radius-sm)] border border-[var(--color-border)] text-sm text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] hover:border-[var(--color-primary)]/40 transition-all font-[var(--font-caption)]"
              title="Account Settings"
            >
              <Icon name="Settings" size={14} color="currentColor" />
              <span className="hidden sm:inline">Settings</span>
            </button>
            <button
              onClick={() => setShowLogoutModal(true)}
              className="flex items-center gap-2 px-3 py-2 rounded-[var(--radius-sm)] border border-[var(--color-error)]/30 text-sm text-[var(--color-error)] hover:bg-[var(--color-error)]/5 hover:border-[var(--color-error)]/50 transition-all font-[var(--font-caption)]"
              title="Logout"
            >
              <Icon name="LogOut" size={14} color="var(--color-error)" />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>

        {/* Section Nav */}
        <div className="flex gap-1 bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] p-1 shadow-[var(--shadow-sm)] mb-6 w-fit flex-wrap">
          {sections?.map(s => (
            <button
              key={s?.id}
              onClick={() => setActiveSection(s?.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded text-sm font-medium font-[var(--font-caption)] transition-all duration-200 ${
                activeSection === s?.id ? 'bg-[var(--color-primary)] text-white shadow-sm' : 'text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]'
              }`}
            >
              <Icon name={s?.icon} size={15} color={activeSection === s?.id ? '#fff' : 'currentColor'} />
              {s?.label}
            </button>
          ))}
        </div>

        {memberError && (
          <div className="flex items-center gap-3 p-4 mb-6 bg-[var(--color-error)]/5 border border-[var(--color-error)]/20 rounded-[var(--radius-md)]">
            <Icon name="AlertCircle" size={16} color="var(--color-error)" />
            <p className="text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)]">{memberError}</p>
          </div>
        )}

        {activeSection === 'overview' && (
          <>
            {!profileCardDismissed && user?.id && (
              <ProfileCompletionCard
                userId={user?.id}
                onDismiss={() => setProfileCardDismissed(true)}
              />
            )}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <MembershipCard memberPackage={memberPackage} loading={memberLoading} error={memberError} />
              <AttendanceCalendar memberId={memberRecord?.id} />
            </div>
          </>
        )}
        {activeSection === 'plans' && <WorkoutDietPlans memberId={memberRecord?.id} />}
        {activeSection === 'invoices' && <InvoiceList memberId={memberRecord?.id} memberName={memberName} />}
        {activeSection === 'referrals' && (
          <QRCodeCard memberId={memberRecord?.id} profileId={user?.id} memberName={memberName} />
        )}
      </main>

      {/* Logout Confirmation Modal */}
      {showLogoutModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => !loggingOut && setShowLogoutModal(false)} />
          <div className="relative bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-lg)] shadow-[var(--shadow-2xl)] p-6 w-full max-w-sm">
            <div className="flex flex-col items-center gap-4 text-center">
              <div className="w-12 h-12 rounded-full bg-[var(--color-error)]/10 flex items-center justify-center">
                <Icon name="LogOut" size={22} color="var(--color-error)" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-[var(--color-foreground)] font-[var(--font-heading)]">Log Out?</h2>
                <p className="text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-1">
                  Are you sure you want to log out of your account?
                </p>
              </div>
              <div className="flex gap-3 w-full">
                <button
                  onClick={() => setShowLogoutModal(false)}
                  disabled={loggingOut}
                  className="flex-1 px-4 py-2 rounded-[var(--radius-sm)] border border-[var(--color-border)] text-sm text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] transition-all font-[var(--font-caption)] disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleLogout}
                  disabled={loggingOut}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-[var(--color-error)] text-white rounded-[var(--radius-sm)] text-sm font-medium font-[var(--font-caption)] hover:opacity-90 transition-opacity disabled:opacity-60"
                >
                  {loggingOut ? (
                    <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Logging out...</>
                  ) : (
                    <><Icon name="LogOut" size={14} color="#fff" />Log Out</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
